/**
 * Générateurs des ITEMS NATIONAUX : 5e, Automatismes, campagne 2026.
 * Même contrat que 2026/6e/automatismes.js (le modèle) : un générateur par item, sa fiche en tête
 * (lue sur la CAPTURE de l'item), ITEMS avec la bonne réponse officielle exacte, CATEGORIES, generate.
 *
 * Une variante garde la tâche, le format (QCM à 4 choix), le domaine de valeurs et les pièges de l'item :
 * chaque mauvaise réponse officielle est l'effet d'une erreur, recalculée sur les valeurs tirées.
 * Tout est construit (FORMATS_TANOED § 0.2) et vérifié par un second calcul (`verifier`, qui lève une
 * erreur) ; `qcm` exige quatre choix distincts après la normalisation du correcteur, la bonne réponse en
 * premier. Choix en texte ou en KaTeX, jamais en image ; figures en SVG maison, sans la réponse.
 */
import { randInt, randomChoice, shuffle, makeQcm, tex, fracTex, formatNumber } from '../../../utils';
import { enLettres, relireEnNombre } from '../../lettres';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: null, label });

/** Le second chemin : une construction qui dérape lève une erreur au lieu de servir une question fausse. */
const verifier = (condition, message) => {
    if (!condition) throw new Error(`evalnat 5e automatismes, ${message}`);
};

/** La normalisation du correcteur (FORMATS_TANOED § 0.1) : casse, accents, première virgule, espaces de milliers. */
const normaliser = (s) => String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(',', '.')
    .replace(/(\d)\s+(?=\d)/g, '$1')
    .trim();

/** Le QCM d'une variante : la bonne réponse en premier, quatre choix distincts après normalisation. */
const qcm = (choix, texte, options) => {
    verifier(choix.length === 4 && choix.every((c) => typeof c === 'string' && c !== '')
        && new Set(choix.map(normaliser)).size === 4, `choix à revoir : ${choix.join(' | ')}`);
    return makeQcm(choix, choix[0], texte, options);
};

/** Écriture française d'un décimal donné en unités de 10^-n, zéros finaux gardés : dec(146188, 4) → « 14,6188 ». */
const dec = (entier, n) => {
    const s = String(entier).padStart(n + 1, '0');
    const partieEntiere = formatNumber(Number(s.slice(0, s.length - n)));
    return n === 0 ? partieEntiere : `${partieEntiere},${s.slice(s.length - n)}`;
};

/** La même écriture dans une formule KaTeX : la virgule entre accolades (FORMATS_TANOED § 0.5bis). */
const decTex = (entier, n) => dec(entier, n).replace(',', '{,}');

/** Second chemin : relit une écriture française (« 1 735,842 ») en unités de 10^-n. */
const relire = (texte, n) => {
    const [entiere, decimale = ''] = String(texte).replace(/\s/g, '').split(',');
    verifier(/^\d+$/.test(entiere) && /^\d*$/.test(decimale) && decimale.length <= n, `« ${texte} » illisible`);
    return Number(entiere) * 10 ** n + Number(decimale.padEnd(n, '0') || '0');
};

const pgcd = (x, y) => (y === 0 ? x : pgcd(y, x % y));
const majuscule = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/** Deux valeurs distinctes : boucle bornée, puis repli déterministe. */
const distinctDe = (interdit, tirer, repli) => {
    for (let essai = 0; essai < 30; essai += 1) {
        const v = tirer();
        if (v !== interdit) return v;
    }
    return repli;
};

const POLICE = 'font-family="system-ui,sans-serif"';
const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const figure = (svg, alt) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: dataUrl(svg), alt } }] });
const px = (v) => (Math.round(v * 10) / 10).toString();

/**
 * Q01 : Trouver la plus grande fraction parmi des fractions de même dénominateur.
 * Item : « Quel est le plus grand nombre parmi les nombres suivants ? » ; 6/5 (juste), 4/5, 5/5, 1/5
 *   (fractions empilées sur la capture).
 * Domaine : quatre fractions de dénominateur 5 : une seule supérieure à 1, tout près (6/5), une égale à 1
 *   (5/5), une un peu inférieure à 1 (4/5) et la fraction unité (1/5). Variante : dénominateur d de 3 à 9,
 *   numérateurs d + 1, d, d − 1 ou d − 2, et 1 (13 variantes).
 * Erreurs : 5/5, le tout pris pour le plus grand nombre possible (une fraction ne dépasserait pas 1) ;
 *   4/5, les fractions qui atteignent ou dépassent 1 écartées, la plus grande des autres choisie ;
 *   1/5, le plus petit numérateur pris pour la plus grande part (la règle des fractions unités,
 *   « plus le nombre est petit, plus la part est grande », appliquée au numérateur).
 */
const L01 = 'Trouver la plus grande fraction parmi des fractions de même dénominateur';
const genQ01 = () => {
    const d = randInt(3, 9);
    const presque = d === 3 ? 2 : d - randInt(1, 2);
    const numerateurs = [d + 1, presque, d, 1];
    const plusGrand = Math.max(...numerateurs.map((n) => n / d));
    verifier(new Set(numerateurs).size === 4 && numerateurs.filter((n) => n / d === plusGrand).length === 1
        && (d + 1) / d === plusGrand, 'Q01 : la plus grande fraction n\'est pas unique');
    const choix = numerateurs.map((n) => fracTex(n, d));
    return qcm(choix, '<p>Quel est le plus grand nombre parmi les nombres suivants ?</p>', opts(L01));
};

/**
 * Q02 : Retrouver l'écriture d'un entier à partir de sa décomposition en unités de mille et unités simples.
 * Item : « 35 × 1 000 + 64 = … » ; 35 064 (juste), 3 564, 6 435, 64 035.
 * Domaine : a × 1 000 + b, a et b à deux chiffres non nuls, distincts : le chiffre des centaines du résultat
 *   est un 0 à écrire.
 * Erreurs : 3 564, a et b écrits côte à côte, le 0 des centaines oublié ; 64 035, les rôles échangés
 *   (b × 1 000 + a) ; 6 435, les rôles échangés et le 0 oublié (b puis a côte à côte).
 */
const L02 = 'Retrouver l\'écriture symbolique d\'un nombre entier à partir de sa décomposition en unités de mille et unités simples';
const deuxChiffresNonNuls = () => randInt(1, 9) * 10 + randInt(1, 9);
const genQ02 = () => {
    const a = deuxChiffresNonNuls();
    const b = distinctDe(a, deuxChiffresNonNuls, a === 64 ? 35 : 64);
    const juste = a * 1000 + b;
    verifier(Number(`${a}0${b}`) === juste, 'Q02 : le zéro des centaines');
    const choix = [juste, Number(`${a}${b}`), Number(`${b}${a}`), b * 1000 + a].map(formatNumber);
    return qcm(choix, `<p>${a} × ${formatNumber(1000)} + ${b} = …</p>`, opts(L02));
};

/**
 * Q03 : Associer l'écriture symbolique d'un nombre entier à sa désignation orale.
 * Item : « Le nombre 110 000 000 se lit : » ; Cent-dix-millions (juste), Cent-dix-mille, Onze milliards,
 *   Onze millions. La capture écrit « Onze milliards » et « Onze millions » sans trait d'union, la base avec :
 *   les variantes suivent l'orthographe rectifiée d'enLettres (des traits d'union partout), comme la bonne
 *   réponse officielle.
 * Domaine : A millions, A à trois chiffres terminé par un seul 0 (A = 10 × k, k de 11 à 99, jamais un multiple
 *   de 10) : un nombre de neuf chiffres, dont sept zéros finaux.
 * Erreurs : Cent-dix-mille, la classe des mille lue à la place de celle des millions (un groupe de trois zéros
 *   oublié) ; Onze millions, les chiffres significatifs 11 lus seuls (le 0 de 110 oublié), classe juste ;
 *   Onze milliards, les chiffres significatifs 11 lus seuls et la classe des milliards prise pour celle des
 *   millions.
 */
const L03 = 'Associer l\'écriture symbolique d\'un nombre entier à sa désignation orale';
const genQ03 = () => {
    const k = deuxChiffresNonNuls();
    const a = 10 * k;
    const n = a * 1000000;
    verifier(relireEnNombre(enLettres(n)) === n && n === k * 10000000, 'Q03 : la lecture');
    const choix = [enLettres(n), enLettres(a * 1000), enLettres(k * 1000000), `${enLettres(k)}-milliards`].map(majuscule);
    return qcm(choix, `<p>Le nombre ${formatNumber(n)} se lit :</p>`, opts(L03));
};

/**
 * Q04 : Connaitre les tables de multiplication.
 * Item : « 8 × … = 48 » ; 6 (juste), 4, 5, 7.
 * Domaine : a × … = a × b, a et b de 6 à 9, distincts (tables difficiles, jamais un carré) ; choix b − 2,
 *   b − 1, b et b + 1, comme l'item (12 variantes).
 * Erreurs : 5 et 7, le voisin dans la table (8 × 5 = 40, 8 × 7 = 56) ; 4, deux rangs à côté (ou le chiffre
 *   des dizaines de 48 : la variante garde la forme b − 2).
 */
const L04 = 'Connaitre les tables de multiplication';
const genQ04 = () => {
    const a = randInt(6, 9);
    const b = distinctDe(a, () => randInt(6, 9), a === 6 ? 8 : 6);
    const produit = a * b;
    const facteurs = [b, b - 2, b - 1, b + 1];
    verifier(facteurs.filter((f) => a * f === produit).length === 1, 'Q04 : le facteur manquant');
    return qcm(facteurs.map(String), `<p>${a} × … = ${produit}</p>`, opts(L04));
};

/**
 * Q05 : Déterminer un complément à 100.
 * Item : « 75 + … = 100 » ; 25 (juste), 30, 35, 40.
 * Domaine : n + … = 100, n à deux chiffres terminé par 5 (n = 10t + 5, t de 2 à 9 ; t = 1 donnerait le
 *   choix 100). Mode auto, TRANCHÉ LE 10/10 (on garde) : le trou est tiré à droite (comme l'item) ou à gauche
 *   (… + 75 = 100), sans quoi le domaine n'a que 8 variantes ; 16 avec.
 * Erreurs : 35, chaque chiffre complété à 10 (7 + 3, 5 + 5), la retenue oubliée (110 − n) ; 30, les
 *   dizaines seules complétées (100 − 70), les unités oubliées ; 40, erreur non identifiée, TRANCHÉ LE 10/10 (on garde)
 *   (piste : « soixante-quinze » lu soixante plus quinze, 100 − 60) : forme reproduite 110 − 10t, soit 15
 *   de plus que la bonne réponse.
 */
const L05 = 'Déterminer un complément à 100';
const genQ05 = () => {
    const t = randInt(2, 9);
    const n = 10 * t + 5;
    const juste = 100 - n;
    const complements = [juste, 100 - 10 * t, 110 - n, 110 - 10 * t];
    verifier(complements.filter((x) => n + x === 100).length === 1 && complements[0] === juste, 'Q05 : le complément');
    const egalite = randInt(0, 1) === 0 ? `${n} + … = 100` : `… + ${n} = 100`;
    return qcm(complements.map(String), `<p>${egalite}</p>`, opts(L05));
};

/**
 * Q06 : Déterminer la fraction d'une surface.
 * Item : « Quelle fraction de la surface est coloriée ? » ; 1/4 (juste), 1/2, 1/3, 3/4. Figure : un triangle
 *   équilatéral partagé par les segments qui joignent les milieux de ses côtés, le triangle du milieu
 *   (renversé) colorié.
 * Domaine : un triangle partagé en quatre triangles superposables par ces segments, celui du milieu colorié :
 *   la réponse est toujours 1/4. Mode auto, TRANCHÉ LE 10/10 (on garde) : seule la figure varie (six formes de triangle,
 *   quatre orientations, 24 variantes) ; une autre fraction demanderait une autre tâche.
 * Erreurs : 1/3, la partie coloriée comparée à la partie non coloriée (1 contre 3) ; 3/4, la fraction de la
 *   partie non coloriée ; 1/2, le rapport des longueurs pris pour celui des aires (le triangle colorié a des
 *   côtés moitié de ceux du grand), ou une estimation à l'œil : TRANCHÉ LE 10/10 (on garde).
 */
const L06 = 'Déterminer la fraction d\'une surface';
const TRIANGLES_Q06 = [
    [[0, 0.866], [1, 0.866], [0.5, 0]], // équilatéral, l'item
    [[0, 1.3], [0.9, 1.3], [0.45, 0]], // isocèle élancé
    [[0, 0.55], [1.6, 0.55], [0.8, 0]], // isocèle aplati
    [[0, 1.1], [1.3, 1.1], [0, 0]], // rectangle
    [[0, 1], [1.4, 1], [0.35, 0]], // quelconque
    [[0, 0.75], [1.1, 0.75], [1.6, 0]], // obtusangle
];
const ALT_Q06 = 'Un grand triangle ; les segments qui joignent les milieux de ses côtés délimitent au centre un triangle colorié.';
const aireTriangle = ([p, q, r]) => Math.abs((q[0] - p[0]) * (r[1] - p[1]) - (r[0] - p[0]) * (q[1] - p[1])) / 2;
const quartDeTour = ([x, y], fois) => (fois === 0 ? [x, y] : quartDeTour([-y, x], fois - 1));
const genQ06 = () => {
    const quarts = randInt(0, 3);
    const tournes = randomChoice(TRIANGLES_Q06).map((p) => quartDeTour(p, quarts));
    const xs = tournes.map((p) => p[0]);
    const ys = tournes.map((p) => p[1]);
    const [xMin, yMin] = [Math.min(...xs), Math.min(...ys)];
    const echelle = Math.min(200 / (Math.max(...xs) - xMin), 170 / (Math.max(...ys) - yMin));
    const P = tournes.map(([x, y]) => [12 + (x - xMin) * echelle, 12 + (y - yMin) * echelle]);
    const M = [[0, 1], [1, 2], [2, 0]].map(([i, j]) => [(P[i][0] + P[j][0]) / 2, (P[i][1] + P[j][1]) / 2]);
    verifier(Math.abs(4 * aireTriangle(M) - aireTriangle(P)) < 1e-6 * aireTriangle(P), 'Q06 : le triangle du milieu n\'est pas le quart');
    const largeur = Math.ceil(Math.max(...P.map((p) => p[0])) + 12);
    const hauteur = Math.ceil(Math.max(...P.map((p) => p[1])) + 12);
    const points = (L) => L.map(([x, y]) => `${px(x)},${px(y)}`).join(' ');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${largeur} ${hauteur}" width="${largeur}" role="img" aria-label="${ALT_Q06}">`
        + `<polygon points="${points(P)}" fill="#ffffff" stroke="#3f2a1e" stroke-width="2" stroke-linejoin="round"/>`
        + `<polygon points="${points(M)}" fill="#3949ab" stroke="#3f2a1e" stroke-width="2" stroke-linejoin="round"/>`
        + '</svg>';
    const choix = [fracTex(1, 4), fracTex(1, 2), fracTex(1, 3), fracTex(3, 4)];
    return qcm(choix, '<p>Quelle fraction de la surface est coloriée ?</p>', { ...opts(L06), elements_additionnels: figure(svg, ALT_Q06) });
};

/**
 * Q07 : Additionner des nombres entiers.
 * Item : « 126 + 99 = … » ; 225 (juste), 227, 115, 226.
 * Domaine : a + 99, a à trois chiffres sans 0 aux dizaines ni aux unités, centaines de 1 à 8 : deux retenues
 *   dans l'addition posée, une somme à trois chiffres.
 * Erreurs : 226, + 100 sans retirer 1 ; 227, + 100 puis + 1 au lieu de − 1 ; 115, l'addition posée sans les
 *   retenues (6 + 9 → 5, 2 + 9 → 1, soit a − 11).
 */
const L07 = 'Additionner des nombres entiers';
const genQ07 = () => {
    const [c, d, u] = [randInt(1, 8), randInt(1, 9), randInt(1, 9)];
    const a = 100 * c + 10 * d + u;
    const juste = a + 99;
    const sansRetenue = 100 * c + 10 * ((d + 9) % 10) + ((u + 9) % 10);
    verifier(a + 100 - 1 === juste && juste < 1000 && sansRetenue === a - 11, 'Q07 : la somme');
    const choix = [juste, a + 101, sansRetenue, a + 100].map(String);
    return qcm(choix, `<p>${a} + 99 = …</p>`, opts(L07));
};

/**
 * Q08 : Multiplier des nombres entiers.
 * Item : « 5 × 43 × 2 = … » ; 430 (juste), 215, 86, 205.
 * Domaine : 5 × n × 2, n à deux chiffres, dizaines de 2 à 9, unités impaires de 3 à 9 (5 × unités a une
 *   retenue).
 * Erreurs : 215, le × 2 oublié (5 × 43) ; 86, le × 5 oublié (43 × 2) ; 205, le × 2 oublié et 5 × 43 posé
 *   sans la retenue (5 × 3 = 15 : 5 écrit, la retenue 1 perdue, soit 50 × dizaines + 5).
 */
const L08 = 'Multiplier des nombres entiers';
const genQ08 = () => {
    const d = randInt(2, 9);
    const u = randomChoice([3, 5, 7, 9]);
    const n = 10 * d + u;
    const juste = 5 * n * 2;
    const sansRetenue = 5 * d * 10 + ((5 * u) % 10);
    verifier(juste === n * 10 && sansRetenue !== 5 * n, 'Q08 : le produit');
    const choix = [juste, 5 * n, 2 * n, sansRetenue].map(String);
    return qcm(choix, `<p>5 × ${n} × 2 = …</p>`, opts(L08));
};

/**
 * Q09 : Déterminer un nombre multiple de 50.
 * Item : « Quel nombre est un multiple de 50 ? » ; 200 (juste), 225, 210, 275.
 * Domaine : quatre nombres de la même centaine h (de 1 à 9) : h00, le multiple de 50 ; h25 et h75 ; un
 *   nombre h-d-0 dont le chiffre des dizaines d n'est ni 0 ni 5.
 * Erreurs : 225 et 275, des multiples de 25 (terminés par 5) pris pour des multiples de 50 ; 210, un
 *   multiple de 10 (terminé par 0) pris pour un multiple de 50.
 */
const L09 = 'Déterminer un nombre multiple de 50';
const genQ09 = () => {
    const h = randInt(1, 9);
    const d = randomChoice([1, 2, 3, 4, 6, 7, 8, 9]);
    const nombres = [100 * h, 100 * h + 25, 100 * h + 10 * d, 100 * h + 75];
    verifier(nombres.filter((x) => x % 50 === 0).length === 1 && nombres[0] % 50 === 0, 'Q09 : le multiple de 50');
    return qcm(nombres.map(String), '<p>Quel nombre est un multiple de 50 ?</p>', opts(L09));
};

/**
 * Q10 : Additionner des nombres décimaux.
 * Item : « 2,8 + 1,8 est égal à… » ; 4,6 (juste), 3,8, 3,16, 3,6.
 * Domaine : a,b + c,b, le même chiffre des dixièmes b de 6 à 9 (b + b dépasse 10 : une retenue), unités
 *   a et c distinctes, a + c ≤ 8.
 * Erreurs : 3,16, unités et dixièmes ajoutés à part, 16 dixièmes écrits « ,16 » ; 3,6, la retenue des
 *   dixièmes oubliée ; 3,8, les unités ajoutées, le chiffre des dixièmes recopié une seule fois (2,8 + 1).
 */
const L10 = 'Additionner des nombres décimaux';
const genQ10 = () => {
    const a = randInt(1, 7);
    const c = distinctDe(a, () => randInt(1, 8 - a), a === 1 ? 2 : 1);
    const b = randInt(6, 9);
    const s = a + c;
    const juste = `${s + 1},${2 * b - 10}`;
    verifier(relire(juste, 1) === (10 * a + b) + (10 * c + b), 'Q10 : la somme');
    const choix = [juste, `${s},${b}`, `${s},${2 * b}`, `${s},${2 * b - 10}`];
    return qcm(choix, `<p>${a},${b} + ${c},${b} est égal à…</p>`, opts(L10));
};

/**
 * Q11 : Diviser un nombre entier par 5.
 * Item : « 525 : 5 = … » ; 105 (juste), 15, 150, 11.
 * Domaine : n = 5 × q, q = 1 0 c (un 0 au rang des dizaines du quotient), c de 2 à 9 : n de 510 à 545.
 *   Mode auto, TRANCHÉ LE 10/10 (on garde) : ce domaine n'a que 8 variantes ; il est élargi à q = 2 0 c (n de 1 010 à
 *   1 045, première division 10 : 5), tiré une fois sur trois (16 variantes).
 * Erreurs : 15, le 0 du quotient oublié ; 150, le 0 mis à la fin ; 11, le chiffre trop petit sauté (2 : 5
 *   impossible, rien d'écrit) et le dernier chiffre divisé seul (5 : 5 = 1), comme 55 : 5 ; pour n terminé
 *   par 0, la même erreur donne 10.
 */
const L11 = 'Diviser un nombre entier par 5';
const genQ11 = () => {
    const h = randomChoice([1, 1, 2]);
    const c = randInt(2, 9);
    const q = 100 * h + c;
    const n = 5 * q;
    verifier(n % 5 === 0 && n / 5 === q && Math.floor(q / 10) % 10 === 0, 'Q11 : le quotient');
    const choix = [q, 10 * h + c, 100 * h + 10 * c, 10 * h + (n % 10) / 5].map(String);
    return qcm(choix, `<p>${formatNumber(n)} : 5 = …</p>`, opts(L11));
};

/**
 * Q12 : Associer une fraction décimale à l'écriture décimale à virgule d'un nombre décimal.
 * Item : « 712/100 = … » (fraction empilée) ; 7,12 (juste), 712,100, 71 200, 0,712.
 * Domaine : N/100, N à trois chiffres non nuls.
 * Erreurs : 712,100, la barre de fraction lue comme une virgule ; 71 200, multiplié par 100 au lieu de
 *   divisé ; 0,712, divisé par 1 000 (la virgule avant tous les chiffres).
 */
const L12 = 'Associer une fraction décimale à l\'écriture décimale à virgule d\'un nombre décimal';
const genQ12 = () => {
    const N = randInt(1, 9) * 100 + randInt(1, 9) * 10 + randInt(1, 9);
    const juste = dec(N, 2);
    verifier(relire(juste, 2) === N, 'Q12 : l\'écriture décimale');
    const choix = [juste, `${N},100`, formatNumber(N * 100), dec(N, 3)];
    return qcm(choix, `<p>${tex(`\\dfrac{${N}}{100} = \\dots`)}</p>`, opts(L12));
};

/**
 * Q13 : Déterminer l'abscisse d'un point sur une portion de droite graduée.
 * Item : « Quel est le nombre indiqué par la flèche ? » ; 14,6188 (juste), 14,69, 14,68, 14,6180. Figure :
 *   repères 14,618 et 14,619, dix graduations entre eux (des dix-millièmes), une de plus avant, quatre
 *   après, une flèche nommée A sur la huitième.
 * Domaine : repères a,bcd et a,bc(d + 1), partie entière à deux chiffres, b et c non nuls, d de 1 à 8, la
 *   flèche à la d-ième graduation : comme dans l'item, le rang de la flèche est le dernier chiffre du repère
 *   de gauche (8), ce qui garde les deux lectures de 14,68 et de 14,69.
 * Erreurs : 14,6180, le repère de gauche recopié avec un 0, les graduations non comptées ; 14,68, le rang de
 *   la graduation écrit aux centièmes derrière 14,6 (le pas pris pour un centième), ou 14,618 lu sans son
 *   chiffre des centièmes ; 14,69, la même erreur sur le repère de droite 14,619 (ou une graduation de trop).
 */
const L13 = 'Déterminer l\'abscisse d\'un point sur une portion de droite graduée';
const svgDroiteQ13 = (gauche, droite, rang, alt) => {
    const pas = 36;
    const x = (i) => 40 + (i + 1) * pas; // graduation i : 0 au repère de gauche, 10 au repère de droite
    const yAxe = 92;
    const largeur = x(14) + 30;
    let traits = '';
    for (let i = -1; i <= 14; i += 1) {
        const repere = i === 0 || i === 10;
        const h = repere ? 13 : 7;
        traits += `<line x1="${x(i)}" y1="${yAxe - h}" x2="${x(i)}" y2="${yAxe + h}" stroke="#1f2937" stroke-width="${repere ? 2.6 : 1.5}"/>`;
    }
    const ecrire = (tx, ty, contenu) => `<text x="${tx}" y="${ty}" font-size="20" text-anchor="middle" ${POLICE} fill="#1f2937">${contenu}</text>`;
    const xf = x(rang);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${largeur} 140" width="${largeur}" role="img" aria-label="${alt}">`
        + `<line x1="20" y1="${yAxe}" x2="${largeur - 20}" y2="${yAxe}" stroke="#1f2937" stroke-width="2"/>`
        + traits
        + ecrire(x(0), yAxe + 40, gauche) + ecrire(x(10), yAxe + 40, droite)
        + ecrire(xf, 24, 'A')
        + `<line x1="${xf}" y1="32" x2="${xf}" y2="${yAxe - 30}" stroke="#1f2937" stroke-width="2.6"/>`
        + `<polygon points="${xf - 8},${yAxe - 32} ${xf + 8},${yAxe - 32} ${xf},${yAxe - 14}" fill="#1f2937"/>`
        + '</svg>';
};
const genQ13 = () => {
    const a = randInt(10, 99);
    const b = randInt(1, 9);
    const c = randInt(1, 9);
    const d = randInt(1, 8);
    const gauche = 1000 * a + 100 * b + 10 * c + d; // en millièmes : 14 618
    const rang = d; // la flèche, comme dans l'item, à la d-ième graduation
    const juste = dec(10 * gauche + rang, 4);
    verifier(relire(juste, 4) === 10 * gauche + rang && relire(dec(gauche + 1, 3), 4) === 10 * gauche + 10
        && rang >= 1 && rang <= 9, 'Q13 : l\'abscisse de la flèche');
    const choix = [juste, dec(10 * gauche, 4), `${a},${b}${d}`, `${a},${b}${d + 1}`];
    const [g, dr] = [dec(gauche, 3), dec(gauche + 1, 3)];
    const alt = `Portion de droite graduée où sont écrits ${g} et ${dr} ; une flèche nommée A désigne une graduation.`;
    return qcm(choix, '<p>Quel est le nombre indiqué par la flèche ?</p>',
        { ...opts(L13), elements_additionnels: figure(svgDroiteQ13(g, dr, rang, alt), alt) });
};

/**
 * Q14 : Approximer un résultat.
 * Item : « Quel nombre est le plus proche du résultat de cette multiplication ? 9,8 × 9,78 ≈ … » ;
 *   100 (juste), 10, 1 000, 10 000.
 * Domaine : x × y, x à un chiffre après la virgule de 9,5 à 9,9, y à deux chiffres après la virgule de 9,51
 *   à 9,99 (centièmes non nuls) : deux facteurs un peu inférieurs à 10, un produit entre 90 et 99 ; les
 *   choix, des puissances de 10, ne changent pas.
 * Erreurs : 10, le produit pris de l'ordre de grandeur des facteurs ; 1 000, la virgule de 9,8 oubliée
 *   (98 × 9,78 ≈ 100 × 10) ; 10 000, la virgule de 9,78 oubliée (9,8 × 978 ≈ 10 × 1 000).
 */
const L14 = 'Approximer un résultat';
const PUISSANCES_Q14 = [100, 10, 1000, 10000];
const plusProcheQ14 = (v) => PUISSANCES_Q14.reduce((m, p) => (Math.abs(p - v) < Math.abs(m - v) ? p : m));
const genQ14 = () => {
    const x = 90 + randInt(5, 9); // en dixièmes
    const y = 900 + 10 * randInt(5, 9) + randInt(1, 9); // en centièmes
    verifier(plusProcheQ14((x * y) / 1000) === 100 && plusProcheQ14((x * y) / 100) === 1000
        && plusProcheQ14((x * y) / 10) === 10000, 'Q14 : l\'ordre de grandeur');
    return qcm(PUISSANCES_Q14.map(formatNumber),
        `<p>Quel nombre est le plus proche du résultat de cette multiplication ?</p><p>${dec(x, 1)} × ${dec(y, 2)} ≈ …</p>`, opts(L14));
};

/**
 * Q15 : Associer différentes écritures d'un nombre décimal (unités de numération).
 * Item : « Écrire le nombre qui correspond à 5 dizaines et 3 centièmes. » ; 50,03 (juste), 50,300, 300,50, 350.
 * Domaine : a dizaines et b centièmes, a et b de 2 à 9 (pluriels), distincts.
 * Erreurs : les centièmes lus comme des centaines (3 → 300) ; 50,300, écrits derrière la virgule ; 300,50,
 *   écrits devant la virgule, les dizaines (50) derrière ; 350, ajoutés aux dizaines (300 + 50).
 */
const L15 = 'Associer différentes écritures d\'un nombre décimal';
const genQ15 = () => {
    const a = randInt(2, 9);
    const b = distinctDe(a, () => randInt(2, 9), a === 3 ? 5 : 3);
    const juste = `${a}0,0${b}`;
    verifier(relire(juste, 2) === 1000 * a + b, 'Q15 : l\'écriture décimale');
    const choix = [juste, `${a}0,${b}00`, `${b}00,${a}0`, String(100 * b + 10 * a)];
    return qcm(choix, `<p>Écrire le nombre qui correspond à ${a} dizaines et ${b} centièmes.</p>`, opts(L15));
};

/**
 * Q16 : Soustraire des nombres décimaux.
 * Item : « 15,3 − 2,4 = … » ; 12,9 (juste), 13,1, 17,7, 13,9.
 * Domaine : A,c − d,e, A de 12 à 29, d à un chiffre plus petit que les unités de A (pas d'autre retenue),
 *   c < e (une retenue aux dixièmes), c + e ≤ 9 (17,7 sans retenue), e − c ≠ 5 (sinon 13,1 et 13,9 se
 *   confondraient).
 * Erreurs : 13,1, le plus petit chiffre des dixièmes ôté du plus grand (4 − 3), sans retenue ; 17,7,
 *   addition au lieu de soustraction ; 13,9, la retenue faite aux dixièmes (13 − 4) mais oubliée aux unités.
 */
const L16 = 'Soustraire des nombres décimaux';
const dixiemesQ16 = () => {
    for (let essai = 0; essai < 30; essai += 1) {
        const c = randInt(1, 4);
        const e = randInt(c + 1, 9 - c);
        if (e - c !== 5) return [c, e];
    }
    return [3, 4];
};
const genQ16 = () => {
    const uA = randInt(2, 9);
    const A = 10 * randInt(1, 2) + uA;
    const d = randInt(1, uA - 1);
    const [c, e] = dixiemesQ16();
    const x = 10 * A + c;
    const y = 10 * d + e;
    const juste = dec(x - y, 1);
    verifier(relire(juste, 1) === x - y && x - y === 10 * (A - d - 1) + (10 + c - e), 'Q16 : la différence');
    const choix = [juste, `${A - d},${e - c}`, dec(x + y, 1), `${A - d},${10 + c - e}`];
    return qcm(choix, `<p>${dec(x, 1)} − ${dec(y, 1)} = …</p>`, opts(L16));
};

/**
 * Q17 : Encadrer un nombre décimal par deux nombres entiers consécutifs.
 * Item : « Quel encadrement de 9,998 par deux nombres entiers est correct ? » ; 9 < 9,998 < 10 (juste),
 *   999 < 9,998 < 1 000, 99 < 9,998 < 100, 9 999 < 9,998 < 10 000.
 * Domaine : x = n,99d, n de 1 à 9, d de 1 à 8 (trois décimales, juste sous l'entier suivant) ; les
 *   encadrements par n, n9, n99 et n999 à gauche, n + 1 suivi de zéros à droite.
 * Erreurs : 99 < … < 100, la virgule déplacée d'un rang (99,98) ; 999 < … < 1 000, de deux rangs (999,8) ;
 *   9 999 < … < 10 000, la virgule ignorée (9 998) et l'encadrement arrondi à 9 999 : en partie non
 *   identifiée, TRANCHÉ LE 10/10 (on garde) ; forme reproduite (n suivi de trois 9, n + 1 suivi de trois 0).
 */
const L17 = 'Encadrer un nombre décimal par deux nombres entiers consécutifs';
const genQ17 = () => {
    const n = randInt(1, 9);
    const x = 1000 * n + 990 + randInt(1, 8); // en millièmes
    const xs = dec(x, 3);
    const cadres = [[n, n + 1], [100 * n + 99, 100 * (n + 1)], [10 * n + 9, 10 * (n + 1)], [1000 * n + 999, 1000 * (n + 1)]];
    const justes = cadres.filter(([bas, haut]) => 1000 * bas < x && x < 1000 * haut);
    verifier(justes.length === 1 && justes[0] === cadres[0] && cadres[0][1] - cadres[0][0] === 1, 'Q17 : l\'encadrement');
    const choix = cadres.map(([bas, haut]) => `${formatNumber(bas)} < ${xs} < ${formatNumber(haut)}`);
    return qcm(choix, `<p>Quel encadrement de ${xs} par deux nombres entiers est correct ?</p>`, opts(L17));
};

/**
 * Q18 : Utiliser l'aspect positionnel de l'écriture décimale d'un nombre.
 * Item : « Dans le nombre 1 735,842 / 2 est le chiffre des… » (deux lignes) ; millièmes. (juste), unités.,
 *   milliers., centièmes.
 * Domaine : un nombre à quatre chiffres avant la virgule et trois après, sept chiffres distincts non nuls ;
 *   le chiffre demandé est le dernier, celui des millièmes, comme dans l'item.
 * Erreurs : unités., le dernier chiffre pris pour celui des unités (la virgule ignorée) ; milliers., les noms
 *   millièmes et milliers confondus ; centièmes., un rang de moins compté après la virgule.
 */
const L18 = 'Utiliser l\'aspect positionnel de l\'écriture décimale d\'un nombre';
const genQ18 = () => {
    const chiffres = shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9]).slice(0, 7);
    const nombre = `${formatNumber(Number(chiffres.slice(0, 4).join('')))},${chiffres.slice(4).join('')}`;
    const cible = String(chiffres[6]);
    const [, decimales] = nombre.split(',');
    verifier(nombre.split('').filter((ch) => ch === cible).length === 1 && decimales.charAt(2) === cible, 'Q18 : le chiffre des millièmes');
    const choix = ['millièmes.', 'unités.', 'milliers.', 'centièmes.'];
    return qcm(choix, `<p>Dans le nombre ${nombre}</p><p>${cible} est le chiffre des…</p>`, opts(L18));
};

/**
 * Q19 : Multiplier un nombre décimal par 100.
 * Item : « 3,6 × 100 = … » ; 360 (juste), 3,600, 300,6, 3600.
 * Domaine : a,b × 100, a et b de 1 à 9 (un chiffre avant la virgule, un après).
 * Erreurs : 3,600, deux zéros ajoutés à la partie décimale ; 300,6, deux zéros ajoutés à la partie entière ;
 *   3600, la virgule effacée et deux zéros ajoutés.
 */
const L19 = 'Multiplier un nombre décimal par 100';
const genQ19 = () => {
    const a = randInt(1, 9);
    const b = randInt(1, 9);
    const juste = String(10 * (10 * a + b));
    verifier(10 * Number(juste) === 100 * (10 * a + b), 'Q19 : le produit');
    const choix = [juste, `${a},${b}00`, `${a}00,${b}`, `${a}${b}00`];
    return qcm(choix, `<p>${a},${b} × 100 = …</p>`, opts(L19));
};

/**
 * Q20 : Associer une fraction à un pourcentage.
 * Item : « 12 × 1/2 permet de calculer… » (fraction empilée) ; 50 % de 12. (juste), 25 % de 12.,
 *   1,2 % de 12., 1,5 % de 12.
 * Domaine : n × 1/2, n pair de 10 à 98 ; la fraction reste 1/2, le cœur de l'item, donc les pourcentages
 *   proposés aussi.
 * Erreurs : 1,2 %, la fraction 1/2 lue 1,2 (la barre prise pour une virgule) ; 25 %, un demi confondu avec
 *   un quart, TRANCHÉ LE 10/10 (on garde) ; 1,5 %, « un demi » confondu avec « un et demi », TRANCHÉ LE 10/10 (on garde).
 */
const L20 = 'Associer une fraction à un pourcentage';
const genQ20 = () => {
    const n = 2 * randInt(5, 49);
    verifier((50 * n) / 100 === n / 2 && (25 * n) / 100 !== n / 2, 'Q20 : le pourcentage');
    const choix = ['50', '25', '1,2', '1,5'].map((p) => `${p} % de ${n}.`);
    return qcm(choix, `<p>${tex(`${n} \\times \\dfrac{1}{2}`)} permet de calculer…</p>`, opts(L20));
};

/**
 * Q21 : Associer différentes écritures d'un nombre décimal (fractions décimales).
 * Item : « 4/10 + 8/100 = … » (fractions empilées) ; 0,48 (juste), 0,84, 408, 480.
 * Domaine : a/10 + b/100, a et b de 1 à 9, distincts.
 * Erreurs : 0,84, dixièmes et centièmes échangés ; 408 et 480, erreurs non identifiées, TRANCHÉ LE 10/10 (on garde) : les deux
 *   numérateurs écrits en un entier avec un 0, entre eux (a0b) ou après (ab0), formes reproduites.
 */
const L21 = 'Associer différentes écritures d\'un nombre décimal';
const genQ21 = () => {
    const a = randInt(1, 9);
    const b = distinctDe(a, () => randInt(1, 9), a === 4 ? 8 : 4);
    const juste = `0,${a}${b}`;
    verifier(relire(juste, 2) === 10 * a + b, 'Q21 : la somme');
    const choix = [juste, `0,${b}${a}`, `${a}0${b}`, `${a}${b}0`];
    return qcm(choix, `<p>${tex(`\\dfrac{${a}}{10} + \\dfrac{${b}}{100} = \\dots`)}</p>`, opts(L21));
};

/**
 * Q22 : Associer un programme de calculs à un calcul en ligne.
 * Item : « Voici un programme de calcul : • Je choisis le nombre 2. • Je lui ajoute 3,5. • Je multiplie le
 *   résultat par 0,1. Quel calcul permet de trouver le résultat ? » ; ( 2 + 3,5 ) × 0,1 (juste),
 *   2 + 3,5 × 0,1, 2 × 0,1 + 3,5, 2 + ( 3,5 × 0,1 ).
 * Domaine : départ a de 2 à 9, ajout b à un chiffre après la virgule (chiffres non nuls), multiplication par
 *   0,1 comme l'item.
 * Erreurs : 2 + 3,5 × 0,1, les parenthèses oubliées (la multiplication ne porte que sur 3,5) ;
 *   2 × 0,1 + 3,5, la multiplication appliquée au nombre de départ, avant l'ajout ; 2 + ( 3,5 × 0,1 ), les
 *   parenthèses autour de la mauvaise partie.
 */
const L22 = 'Associer un programme de calculs à un calcul en ligne';
const genQ22 = () => {
    const a = randInt(2, 9);
    const b = 10 * randInt(1, 9) + randInt(1, 9); // en dixièmes
    const bs = dec(b, 1);
    // en centièmes : le programme pas à pas (a, puis + b dixièmes, puis × 0,1), puis la valeur de chaque calcul
    const programme = (100 * a + 10 * b) / 10;
    const valeurs = [(100 * a + 10 * b) / 10, 100 * a + (10 * b) / 10, (100 * a) / 10 + 10 * b, 100 * a + (10 * b) / 10];
    verifier(valeurs.filter((v) => v === programme).length === 1 && valeurs[0] === programme, 'Q22 : le calcul du programme');
    const choix = [`( ${a} + ${bs} ) × 0,1`, `${a} + ${bs} × 0,1`, `${a} × 0,1 + ${bs}`, `${a} + ( ${bs} × 0,1 )`];
    const texte = '<p>Voici un programme de calcul :</p>'
        + `<p>• Je choisis le nombre ${a}.</p><p>• Je lui ajoute ${bs}.</p><p>• Je multiplie le résultat par 0,1.</p>`
        + '<p>Quel calcul permet de trouver le résultat ?</p>';
    return qcm(choix, texte, opts(L22));
};

/**
 * Q23 : Effectuer un calcul en ligne en respectant les priorités de calcul.
 * Item : « 6 + 4 × 2 = … » ; 14 (juste), 20, 12, 16.
 * Domaine : a + b × c, a et b de 2 à 9, c de 2 à 5, tous distincts (sinon deux choix pourraient se confondre).
 * Erreurs : 20, le calcul de gauche à droite ((6 + 4) × 2) ; 12, × lu comme + (6 + 4 + 2) ; 16, erreur non
 *   identifiée, TRANCHÉ LE 10/10 (on garde) (piste : la multiplication faite avec le premier nombre, 6 × 2 + 4) : forme
 *   reproduite a × c + b.
 */
const L23 = 'Effectuer un calcul en ligne en respectant les priorités de calcul';
const genQ23 = () => {
    const c = randInt(2, 5);
    const [a, b] = shuffle([2, 3, 4, 5, 6, 7, 8, 9].filter((v) => v !== c)).slice(0, 2);
    const juste = a + b * c;
    const produitDAbord = [b, c].reduce((p, v) => p * v, 1) + a;
    verifier(produitDAbord === juste, 'Q23 : la priorité');
    const choix = [juste, (a + b) * c, a + b + c, a * c + b].map(String);
    return qcm(choix, `<p>${a} + ${b} × ${c} = …</p>`, opts(L23));
};

/**
 * Q24 : Déterminer l'abscisse d'un point sur une demi-droite graduée.
 * Item : « Quel est le nombre désigné par la flèche ? » ; 11/10 (juste), 101/100, 1/10, 11/100 (fractions
 *   empilées). Figure : une demi-droite graduée comme une règle, 0 et 1 écrits, les dixièmes en grands traits,
 *   les centièmes en petits, la flèche sur le premier dixième après 1.
 * Domaine : la flèche sur le k-ième dixième après 1, k de 1 à 9. Mode auto, TRANCHÉ LE 10/10 (on garde) : ces 9 positions ne
 *   suffisent pas ; s'y ajoute la flèche juste après 2 (0, 1 et 2 écrits, k de 1 à 3, une fois sur quatre),
 *   12 variantes.
 * Erreurs : 101/100, le pas des grands traits pris pour un centième (1 + 1/100) ; 1/10, l'unité oubliée
 *   (seulement ce qui suit 1) ; 11/100, les onze grands traits comptés depuis 0 mais lus en centièmes.
 */
const L24 = 'Déterminer l\'abscisse d\'un point sur une demi-droite graduée';
const ALT_Q24 = 'Demi-droite graduée à partir de 0, les nombres entiers écrits ; une flèche désigne une graduation.';
const svgDemiDroiteQ24 = (entiers, cible) => { // cible : l'abscisse de la flèche, en centièmes
    const fin = cible + 5;
    const gauche = 24;
    const longueur = 760;
    const pas = longueur / fin; // un centième
    const x = (i) => gauche + i * pas;
    const yAxe = 74;
    let traits = '';
    for (let i = 0; i <= fin; i += 1) {
        let trait = [8, '#9ca3af', 1];
        if (i % 100 === 0) trait = [30, '#dc2626', 2.4];
        else if (i % 10 === 0) trait = [22, '#111827', 2];
        else if (i % 5 === 0) trait = [14, '#4b5563', 1.2];
        traits += `<line x1="${px(x(i))}" y1="${yAxe}" x2="${px(x(i))}" y2="${yAxe - trait[0]}" stroke="${trait[1]}" stroke-width="${trait[2]}"/>`;
    }
    let nombres = '';
    for (let e = 0; e <= entiers; e += 1) {
        nombres += `<text x="${px(x(100 * e))}" y="${yAxe - 38}" font-size="17" font-weight="700" text-anchor="middle" ${POLICE} fill="#111827">${e}</text>`;
    }
    const xf = x(cible);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${2 * gauche + longueur} 132" width="${2 * gauche + longueur}" role="img" aria-label="${ALT_Q24}">`
        + `<line x1="${gauche}" y1="${yAxe}" x2="${px(x(fin))}" y2="${yAxe}" stroke="#111827" stroke-width="2"/>`
        + traits + nombres
        + `<line x1="${px(xf)}" y1="${yAxe + 52}" x2="${px(xf)}" y2="${yAxe + 20}" stroke="#2563eb" stroke-width="5"/>`
        + `<polygon points="${px(xf - 9)},${yAxe + 22} ${px(xf + 9)},${yAxe + 22} ${px(xf)},${yAxe + 5}" fill="#2563eb"/>`
        + '</svg>';
};
const genQ24 = () => {
    const m = randomChoice([1, 1, 1, 2]);
    const k = m === 1 ? randInt(1, 9) : randInt(1, 3);
    const dixiemes = 10 * m + k;
    verifier(dixiemes % 10 !== 0 && 10 * dixiemes === 100 * m + 10 * k, 'Q24 : l\'abscisse de la flèche');
    const choix = [fracTex(dixiemes, 10), fracTex(100 * m + k, 100), fracTex(k, 10), fracTex(dixiemes, 100)];
    return qcm(choix, '<p>Quel est le nombre désigné par la flèche ?</p>',
        { ...opts(L24), elements_additionnels: figure(svgDemiDroiteQ24(m, 10 * dixiemes), ALT_Q24) });
};

/**
 * Q25 : Intercaler un nombre décimal entre deux nombres décimaux.
 * Item : « 6,522 < … < 6,523 / Quel nombre décimal peut-on écrire à la place des … ? » ; 6,5224 (juste),
 *   6,5220, 6,5201, 6,5230.
 * Domaine : a,bcd < … < a,bc(d + 1), chiffres non nuls, d de 1 à 8 ; la réponse a,bcde, e de 1 à 9.
 * Erreurs : 6,5220, la borne de gauche complétée par un 0 (égale, pas plus grande) ; 6,5230, la borne de
 *   droite complétée par un 0 ; 6,5201, erreur non identifiée, TRANCHÉ LE 10/10 (on garde) (piste : 6,52 suivi de 01, le
 *   chiffre des millièmes perdu) : forme reproduite a,bc01, toujours inférieure à la borne de gauche.
 */
const L25 = 'Intercaler un nombre décimal entre deux nombres décimaux';
const genQ25 = () => {
    const [a, b, c, d, e] = [randInt(1, 9), randInt(1, 9), randInt(1, 9), randInt(1, 8), randInt(1, 9)];
    const bas = 1000 * a + 100 * b + 10 * c + d; // en millièmes
    const haut = bas + 1;
    const choix = [dec(10 * bas + e, 4), dec(10 * bas, 4), `${a},${b}${c}01`, dec(10 * haut, 4)];
    const entre = choix.filter((ch) => 10 * bas < relire(ch, 4) && relire(ch, 4) < 10 * haut);
    verifier(entre.length === 1 && entre[0] === choix[0], 'Q25 : le nombre intercalé');
    const texte = `<p>${tex(`${decTex(bas, 3)} \\lt \\dots \\lt ${decTex(haut, 3)}`)}</p>`
        + '<p>Quel nombre décimal peut-on écrire à la place des … ?</p>';
    return qcm(choix, texte, opts(L25));
};

/**
 * Q26 : Calculer le volume d'un pavé droit en utilisant une formule.
 * Item : « Quel est le volume d'un pavé droit dont les côtés mesurent 6 cm, 5 cm et 2 cm ? » ; 60 cm³
 *   (juste), 13 cm³, 30 cm³, 3 600 cm³.
 * Domaine : trois longueurs entières distinctes de 2 à 9 cm, un volume d'au plus 100 cm³.
 * Erreurs : 13 cm³, les trois longueurs ajoutées ; 30 cm³, les deux premières seulement multipliées (l'aire
 *   d'une face) ; 3 600 cm³, les aires des trois faces multipliées (30 × 12 × 10), soit le carré du volume.
 */
const L26 = 'Calculer le volume d\'un pavé droit en utilisant une formule';
const dimensionsQ26 = () => {
    for (let essai = 0; essai < 60; essai += 1) {
        const [a, b, c] = shuffle([2, 3, 4, 5, 6, 7, 8, 9]).slice(0, 3);
        const v = a * b * c;
        if (v <= 100 && new Set([v, a + b + c, a * b, v * v]).size === 4) return [a, b, c];
    }
    return [6, 5, 2];
};
const genQ26 = () => {
    const [a, b, c] = dimensionsQ26();
    const v = a * b * c;
    verifier(v * v === (a * b) * (a * c) * (b * c) && v <= 100, 'Q26 : le volume');
    const choix = [v, a + b + c, a * b, v * v].map((x) => `${formatNumber(x)} cm³`);
    return qcm(choix, `<p>Quel est le volume d'un pavé droit dont les côtés mesurent ${a} cm, ${b} cm et ${c} cm ?</p>`, opts(L26));
};

/**
 * Q27 : Additionner des fractions de même dénominateur.
 * Item : « 3/7 + 3/7 = … » (fractions empilées) ; 6/7 (juste), 3/7, 6/14, 9/49.
 * Domaine : a/b + a/b, la même fraction deux fois, b impair de 5 à 13, a de 2 à (b − 1)/2, premier avec b :
 *   une somme irréductible inférieure à 1 (14 variantes).
 * Erreurs : 6/14, numérateurs et dénominateurs ajoutés ; 9/49, numérateurs et dénominateurs multipliés ;
 *   3/7, numérateurs et dénominateurs ajoutés puis 6/14 simplifié (ou la fraction recopiée).
 */
const L27 = 'Additionner des fractions de même dénominateur';
const genQ27 = () => {
    const b = randomChoice([5, 7, 9, 11, 13]);
    const tirer = () => randInt(2, (b - 1) / 2);
    let a = tirer();
    for (let essai = 0; essai < 30 && pgcd(a, b) !== 1; essai += 1) a = tirer();
    if (pgcd(a, b) !== 1) a = 2; // 2 est premier avec tout dénominateur impair
    verifier((a * b + a * b) * b === 2 * a * b * b && 2 * a < b && pgcd(2 * a, b) === 1, 'Q27 : la somme');
    const choix = [fracTex(2 * a, b), fracTex(a, b), fracTex(2 * a, 2 * b), fracTex(a * a, b * b)];
    return qcm(choix, `<p>${tex(`\\dfrac{${a}}{${b}} + \\dfrac{${a}}{${b}} = \\dots`)}</p>`, opts(L27));
};

/**
 * Q28 : Multiplier un nombre entier par 0,1.
 * Item : « 780 × 0,1 = … » ; 780 : 10 (juste), 780 : 1/10, 780 × 0,01, 780 × 1/100 (fractions empilées).
 * Domaine : n × 0,1, n à trois chiffres, multiple de 10 et pas de 100 ; les quatre écritures en KaTeX.
 * Erreurs : 780 : 1/10, 0,1 écrit 1/10 mais multiplier et diviser échangés ; 780 × 0,01 et 780 × 1/100, le
 *   dixième pris pour un centième.
 */
const L28 = 'Multiplier un nombre entier par 0,1';
const genQ28 = () => {
    const n = 10 * deuxChiffresNonNuls();
    // en centièmes : n × 0,1 = n × 1/10, puis la valeur de n : 10, n : 1/10, n × 0,01 et n × 1/100
    const vise = (100 * n * 1) / 10;
    const valeurs = [(100 * n) / 10, 100 * n * 10, (100 * n) / 100, (100 * n) / 100];
    verifier(valeurs.filter((v) => v === vise).length === 1 && valeurs[0] === vise, 'Q28 : n × 0,1');
    const choix = [
        tex(`${n} : 10`),
        tex(`${n} : \\dfrac{1}{10}`),
        tex(`${n} \\times 0{,}01`),
        tex(`${n} \\times \\dfrac{1}{100}`),
    ];
    return qcm(choix, `<p>${n} × 0,1 = …</p>`, opts(L28));
};

/**
 * Q29 : Connaitre la définition de la fraction a/b comme le nombre qui, multiplié par b, est égal à a.
 * Item : « À quel nombre est égal : « cinq fois trois cinquièmes » ? » ; 3 (juste), 15, 5, 3/5 (empilée).
 * Domaine : « a fois b a-ièmes », a de 5 à 10 (des cinquièmes aux dixièmes), b de 2 à a − 1, premier avec a.
 * Erreurs : 15, le dénominateur oublié (5 × 3) ; 3/5, la fraction recopiée, la multiplication oubliée ; 5,
 *   erreur non identifiée, TRANCHÉ LE 10/10 (on garde) (piste : 5 × 3 = 15 divisé par le numérateur 3 au lieu de 5) : forme
 *   reproduite a.
 */
const L29 = 'Connaitre la définition de la fraction a/b comme le nombre qui multiplié par b est égal à a (avec b différent de zéro)';
const ORDINAUX_Q29 = { 5: 'cinquièmes', 6: 'sixièmes', 7: 'septièmes', 8: 'huitièmes', 9: 'neuvièmes', 10: 'dixièmes' };
const genQ29 = () => {
    const a = randInt(5, 10);
    let b = randInt(2, a - 1);
    for (let essai = 0; essai < 30 && pgcd(a, b) !== 1; essai += 1) b = randInt(2, a - 1);
    if (pgcd(a, b) !== 1) b = a - 1; // deux entiers consécutifs sont premiers entre eux
    verifier((a * b) % a === 0 && (a * b) / a === b, 'Q29 : a fois b a-ièmes');
    const choix = [String(b), String(a * b), String(a), fracTex(b, a)];
    return qcm(choix, `<p>À quel nombre est égal : « ${enLettres(a)} fois ${enLettres(b)} ${ORDINAUX_Q29[a]} » ?</p>`, opts(L29));
};

/**
 * Q30 : Associer différentes écritures d'un nombre entier.
 * Item : « « 4 dizaines de milliards, 8 centaines de millions et 40 dizaines » correspond au nombre… » ;
 *   40 800 000 400 (juste), 40 800 400, 484 000 000 000, 40 800 000 40.
 * Domaine : a dizaines de milliards, b centaines de millions et c dizaines, a et b de 2 à 9, c multiple de 10
 *   de 10 à 90 (c dizaines font c/10 centaines).
 * Erreurs : 40 800 400, les trois nombres écrits à la suite, sans la classe des mille vide ; 484 000 000 000,
 *   les chiffres 4, 8 et 4 côte à côte devant les zéros des milliards ; 40 800 000 40, les 40 dizaines écrites
 *   40 (le rang des dizaines oublié).
 */
const L30 = 'Associer différentes écritures d\'un nombre entier';
const genQ30 = () => {
    const [a, b, d] = [randInt(2, 9), randInt(2, 9), randInt(1, 9)];
    const c = 10 * d;
    const valeur = a * 1e10 + b * 1e8 + c * 10;
    const juste = formatNumber(valeur);
    verifier(juste === `${a}0 ${b}00 000 ${d}00` && relire(juste, 0) === valeur, 'Q30 : le nombre');
    const choix = [juste, `${a}0 ${b}00 ${d}00`, `${a}${b}${d} 000 000 000`, `${a}0 ${b}00 000 ${c}`];
    return qcm(choix, `<p>« ${a} dizaines de milliards, ${b} centaines de millions et ${c} dizaines » correspond au nombre…</p>`, opts(L30));
};

const GENERATORS = {
    q01_qcm_unique: genQ01,
    q02_qcm_unique: genQ02,
    q03_qcm_unique: genQ03,
    q04_qcm_unique: genQ04,
    q05_qcm_unique: genQ05,
    q06_qcm_unique: genQ06,
    q07_qcm_unique: genQ07,
    q08_qcm_unique: genQ08,
    q09_qcm_unique: genQ09,
    q10_qcm_unique: genQ10,
    q11_qcm_unique: genQ11,
    q12_qcm_unique: genQ12,
    q13_qcm_unique: genQ13,
    q14_qcm_unique: genQ14,
    q15_qcm_unique: genQ15,
    q16_qcm_unique: genQ16,
    q17_qcm_unique: genQ17,
    q18_qcm_unique: genQ18,
    q19_qcm_unique: genQ19,
    q20_qcm_unique: genQ20,
    q21_qcm_unique: genQ21,
    q22_qcm_unique: genQ22,
    q23_qcm_unique: genQ23,
    q24_qcm_unique: genQ24,
    q25_qcm_unique: genQ25,
    q26_qcm_unique: genQ26,
    q27_qcm_unique: genQ27,
    q28_qcm_unique: genQ28,
    q29_qcm_unique: genQ29,
    q30_qcm_unique: genQ30,
};

export const ITEMS = [
    { questionNum: 'Q01', formatKey: 'q01_qcm_unique', libelle: L01, bonneReponseOfficielle: '6/5' },
    { questionNum: 'Q02', formatKey: 'q02_qcm_unique', libelle: L02, bonneReponseOfficielle: '35 064' },
    { questionNum: 'Q03', formatKey: 'q03_qcm_unique', libelle: L03, bonneReponseOfficielle: 'Cent-dix-millions' },
    { questionNum: 'Q04', formatKey: 'q04_qcm_unique', libelle: L04, bonneReponseOfficielle: '6' },
    { questionNum: 'Q05', formatKey: 'q05_qcm_unique', libelle: L05, bonneReponseOfficielle: '25' },
    { questionNum: 'Q06', formatKey: 'q06_qcm_unique', libelle: L06, bonneReponseOfficielle: '1/4' },
    { questionNum: 'Q07', formatKey: 'q07_qcm_unique', libelle: L07, bonneReponseOfficielle: '225' },
    { questionNum: 'Q08', formatKey: 'q08_qcm_unique', libelle: L08, bonneReponseOfficielle: '430' },
    { questionNum: 'Q09', formatKey: 'q09_qcm_unique', libelle: L09, bonneReponseOfficielle: '200' },
    { questionNum: 'Q10', formatKey: 'q10_qcm_unique', libelle: L10, bonneReponseOfficielle: '4,6' },
    { questionNum: 'Q11', formatKey: 'q11_qcm_unique', libelle: L11, bonneReponseOfficielle: '105' },
    { questionNum: 'Q12', formatKey: 'q12_qcm_unique', libelle: L12, bonneReponseOfficielle: '7,12' },
    { questionNum: 'Q13', formatKey: 'q13_qcm_unique', libelle: L13, bonneReponseOfficielle: '14,6188' },
    { questionNum: 'Q14', formatKey: 'q14_qcm_unique', libelle: L14, bonneReponseOfficielle: '100' },
    { questionNum: 'Q15', formatKey: 'q15_qcm_unique', libelle: L15, bonneReponseOfficielle: '50,03' },
    { questionNum: 'Q16', formatKey: 'q16_qcm_unique', libelle: L16, bonneReponseOfficielle: '12,9' },
    { questionNum: 'Q17', formatKey: 'q17_qcm_unique', libelle: L17, bonneReponseOfficielle: '9 < 9,998 < 10' },
    { questionNum: 'Q18', formatKey: 'q18_qcm_unique', libelle: L18, bonneReponseOfficielle: 'millièmes.' },
    { questionNum: 'Q19', formatKey: 'q19_qcm_unique', libelle: L19, bonneReponseOfficielle: '360' },
    { questionNum: 'Q20', formatKey: 'q20_qcm_unique', libelle: L20, bonneReponseOfficielle: '50 % de 12.' },
    { questionNum: 'Q21', formatKey: 'q21_qcm_unique', libelle: L21, bonneReponseOfficielle: '0,48' },
    { questionNum: 'Q22', formatKey: 'q22_qcm_unique', libelle: L22, bonneReponseOfficielle: '( 2 + 3,5 ) × 0,1' },
    { questionNum: 'Q23', formatKey: 'q23_qcm_unique', libelle: L23, bonneReponseOfficielle: '14' },
    { questionNum: 'Q24', formatKey: 'q24_qcm_unique', libelle: L24, bonneReponseOfficielle: '11/10' },
    { questionNum: 'Q25', formatKey: 'q25_qcm_unique', libelle: L25, bonneReponseOfficielle: '6,5224' },
    { questionNum: 'Q26', formatKey: 'q26_qcm_unique', libelle: L26, bonneReponseOfficielle: '60 cm³' },
    { questionNum: 'Q27', formatKey: 'q27_qcm_unique', libelle: L27, bonneReponseOfficielle: '6/7' },
    { questionNum: 'Q28', formatKey: 'q28_qcm_unique', libelle: L28, bonneReponseOfficielle: '780 : 10' },
    { questionNum: 'Q29', formatKey: 'q29_qcm_unique', libelle: L29, bonneReponseOfficielle: '3' },
    { questionNum: 'Q30', formatKey: 'q30_qcm_unique', libelle: L30, bonneReponseOfficielle: '40 800 000 400' },
];

export const CATEGORIES = ITEMS.map(({ questionNum, formatKey, libelle }) => ({
    id: questionNum.toLowerCase(),
    label: `${questionNum} · ${libelle}`,
    formatsList: [{ key: formatKey, label: 'QCM' }],
}));

export const generate = (typeExercice) => {
    const key = GENERATORS[typeExercice] ? typeExercice : randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
